<template>
  <!-- Premium Handover History Modal -->
  <div class="modal fade" :class="{ show: show }" :style="{ display: show ? 'block' : 'none' }" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-xl" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="bx bx-history me-2"></i>
            Premium Payment Handover History
          </h5>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>

        <div class="modal-body">
          <!-- Date Filter -->
          <div class="card mb-4">
            <div class="card-body">
              <div class="row g-3">
                <div class="col-md-3">
                  <label class="form-label">Date From</label>
                  <input v-model="filters.dateFrom" type="date" class="form-control">
                </div>
                <div class="col-md-3">
                  <label class="form-label">Date To</label>
                  <input v-model="filters.dateTo" type="date" class="form-control">
                </div>
                <div class="col-md-4">
                  <label class="form-label">Collector (Optional)</label>
                  <input v-model="filters.collectorName" type="text" class="form-control" placeholder="Filter by collector name">
                </div>
                <div class="col-md-2">
                  <label class="form-label">&nbsp;</label>
                  <div>
                    <button @click="loadHistory" class="btn btn-primary w-100" :disabled="loading">
                      <span v-if="loading">
                        <div class="spinner-border spinner-border-sm me-2" role="status"></div>
                        Loading...
                      </span>
                      <span v-else>
                        <i class="bx bx-search me-1"></i> Load History
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Summary Cards -->
          <div class="row mb-4" v-if="history.length > 0">
            <div class="col-md-3">
              <div class="card bg-primary text-white">
                <div class="card-body text-center">
                  <h4 class="mb-1 text-white">{{ totalHandovers }}</h4>
                  <small>Total Handovers</small>
                </div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="card bg-success text-white">
                <div class="card-body text-center">
                  <h4 class="mb-1  text-white">{{ formatCurrency(totalAmount) }}</h4>
                  <small>Total Amount</small>
                </div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="card bg-info text-white">
                <div class="card-body text-center">
                  <h4 class="mb-1  text-white">{{ uniqueCollectors }}</h4>
                  <small>Unique Collectors</small>
                </div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="card bg-warning text-white">
                <div class="card-body text-center">
                  <h4 class="mb-1  text-white">{{ totalTransactions }}</h4>
                  <small>Total Transactions</small>
                </div>
              </div>
            </div>
          </div>

          <!-- History Table -->
          <div class="table-responsive">
            <table class="table table-bordered table-hover">
              <thead class="table-light">
              <tr>
                <th width="120">Handover Date</th>
                <th>Collector Name</th>
                <th class="text-center" width="100">Transactions</th>
                <th class="text-end" width="120">Expected</th>
                <th class="text-end" width="120">Actual</th>
                <th class="text-end" width="100">Variance</th>
                <th>Received By</th>
                <th width="120">Location</th>
                <th class="text-center" width="100">Status</th>
                <th width="200">Notes</th>
              </tr>
              </thead>
              <tbody>
              <tr v-if="loading">
                <td colspan="10" class="text-center py-4">
                  <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                  <p class="mt-2 mb-0">Loading handover history...</p>
                </td>
              </tr>
              <tr v-else-if="history.length === 0">
                <td colspan="10" class="text-center py-4 text-muted">
                  <i class="bx bx-info-circle me-2"></i>
                  No handover history found for the selected period
                </td>
              </tr>
              <tr v-else v-for="handover in history" :key="handover.handover_id">
                <td>
                  <div>
                    <strong>{{ formatDate(handover.handover_date) }}</strong>
                    <br>
                    <small class="text-muted">{{ formatTime(handover.handover_date) }}</small>
                  </div>
                </td>
                <td>
                  <div class="d-flex align-items-center">
                    <div class="avatar-sm me-2">
                        <span class="avatar-title rounded-circle bg-primary text-white">
                          {{ getInitials(handover.collector_name) }}
                        </span>
                    </div>
                    <div>
                      <strong>{{ handover.collector_name }}</strong>
                      <br>
                      <small class="text-muted">{{ handover.handover_reference }}</small>
                    </div>
                  </div>
                </td>
                <td class="text-center">
                  <span class="badge bg-light text-dark">{{ handover.total_transactions }}</span>
                </td>
                <td class="text-end">
                  <strong class="text-primary">{{ formatCurrency(handover.total_amount) }}</strong>
                </td>
                <td class="text-end">
                  <strong :class="getActualAmountClass(handover)">
                    {{ formatCurrency(handover.actual_amount_received || handover.total_amount) }}
                  </strong>
                </td>
                <td class="text-end">
                  <span v-if="Math.abs(handover.variance_amount) > 0.01"
                        :class="getVarianceClass(handover.variance_amount)">
                    {{ formatCurrency(handover.variance_amount) }}
                  </span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td>
                  <strong>{{ handover.received_by_name }}</strong>
                </td>
                <td>
                  <small class="text-muted">
                    <div v-if="handover.woreda_name">{{ handover.woreda_name }}</div>
                    <div v-if="handover.zone_name">{{ handover.zone_name }}</div>
                  </small>
                </td>
                <td class="text-center">
                    <span :class="getStatusBadge(handover.status)">
                      {{ formatStatus(handover.status) }}
                    </span>
                </td>
                <td>
                    <span v-if="handover.notes" class="text-muted small">
                      {{ handover.notes.length > 80 ? handover.notes.substring(0, 80) + '...' : handover.notes }}
                    </span>
                  <span v-else class="text-muted">No notes</span>
                </td>
              </tr>
              </tbody>
            </table>
          </div>

          <!-- Export Button -->
          <div class="text-end mt-3" v-if="history.length > 0">
            <button @click="exportHistory" class="btn btn-outline-success">
              <i class="bx bx-download me-1"></i>
              Export to CSV
            </button>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">Close</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal Backdrop -->
  <div v-if="show" class="modal-backdrop fade show"></div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: "PremiumHandoverHistoryModal",
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      history: [],
      filters: {
        dateFrom: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
        dateTo: new Date().toISOString().split('T')[0], // Today
        collectorName: ''
      }
    };
  },

  computed: {
    totalHandovers() {
      return this.history.length;
    },

    totalAmount() {
      return this.history.reduce((sum, h) => sum + parseFloat(h.total_amount || 0), 0);
    },

    totalTransactions() {
      return this.history.reduce((sum, h) => sum + parseInt(h.total_transactions || 0), 0);
    },

    uniqueCollectors() {
      const collectors = new Set(this.history.map(h => h.collector_name));
      return collectors.size;
    }
  },

  watch: {
    show(newVal) {
      if (newVal) {
        this.loadHistory();
      }
    }
  },

  methods: {
    async loadHistory() {
      this.loading = true;

      const data = {
        "function": "getPremiumHandoverHistory",
        "dateFrom": this.filters.dateFrom,
        "dateTo": this.filters.dateTo,
        "collectorName": this.filters.collectorName
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.history = res.data.data || [];
        } else {
          this.showError(res.data.message || 'Failed to load handover history');
        }
      } catch (e) {
        this.showError('Failed to load handover history');
        console.error("Failed to load handover history:", e);
      } finally {
        this.loading = false;
      }
    },

    exportHistory() {
      if (this.history.length === 0) return;

      try {
        // Helper function to escape CSV values
        const escapeCSVValue = (value) => {
          if (value === null || value === undefined) return '';
          const stringValue = String(value);
          // If value contains comma, quote, or newline, wrap in quotes and escape internal quotes
          if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        };

        // Create CSV content
        const headers = [
          'Handover Date',
          'Handover Reference',
          'Collector Name',
          'Total Transactions',
          'Expected Amount (ETB)',
          'Actual Amount (ETB)',
          'Variance Amount (ETB)',
          'Variance Type',
          'Received By',
          'Status',
          'Woreda',
          'Zone',
          'Region',
          'Collection Period From',
          'Collection Period To',
          'Notes'
        ];

        const csvRows = this.history.map(handover => [
          escapeCSVValue(this.formatDateTime(handover.handover_date)),
          escapeCSVValue(handover.handover_reference),
          escapeCSVValue(handover.collector_name),
          escapeCSVValue(handover.total_transactions),
          escapeCSVValue(handover.total_amount),
          escapeCSVValue(handover.actual_amount_received || handover.total_amount),
          escapeCSVValue(handover.variance_amount || 0),
          escapeCSVValue(handover.variance_type || 'exact'),
          escapeCSVValue(handover.received_by_name),
          escapeCSVValue(this.formatStatus(handover.status)),
          escapeCSVValue(handover.woreda_name || ''),
          escapeCSVValue(handover.zone_name || ''),
          escapeCSVValue(handover.region_name || ''),
          escapeCSVValue(handover.collection_period_from || ''),
          escapeCSVValue(handover.collection_period_to || ''),
          escapeCSVValue(handover.notes || '')
        ]);

        const csvContent = [
          headers.join(','),
          ...csvRows.map(row => row.join(','))
        ].join('\n');

        // Create and download CSV file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        const fileName = `premium_handover_history_${this.filters.dateFrom}_to_${this.filters.dateTo}.csv`;

        link.setAttribute('href', url);
        link.setAttribute('download', fileName);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        console.log('CSV export completed successfully');
      } catch (e) {
        console.error('CSV export error:', e);
        this.showError('Failed to export history to CSV');
      }
    },

    showError(message) {
      // You can implement a toast notification or alert here
      console.error(message);
    },

    closeModal() {
      this.$emit('close');
    },

    getInitials(name) {
      if (!name) return 'N/A';
      return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
    },

    formatCurrency(amount) {
      if (!amount || amount === 0) return 'ETB 0.00';
      return 'ETB ' + parseFloat(amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    formatDate(datetime) {
      if (!datetime) return 'N/A';
      return new Date(datetime).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatTime(datetime) {
      if (!datetime) return 'N/A';
      return new Date(datetime).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatDateTime(datetime) {
      if (!datetime) return 'N/A';
      return new Date(datetime).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatStatus(status) {
      const statusMap = {
        'completed': 'Completed',
        'discrepancy': 'Discrepancy',
        'cancelled': 'Cancelled'
      };
      return statusMap[status] || status;
    },

    getStatusBadge(status) {
      const badges = {
        'completed': 'badge bg-success',
        'discrepancy': 'badge bg-warning text-dark',
        'cancelled': 'badge bg-danger'
      };
      return badges[status] || 'badge bg-secondary';
    },

    getActualAmountClass(handover) {
      const variance = parseFloat(handover.variance_amount || 0);
      if (Math.abs(variance) > 0.01) {
        return variance > 0 ? 'text-success' : 'text-danger';
      }
      return 'text-success';
    },

    getVarianceClass(varianceAmount) {
      const variance = parseFloat(varianceAmount || 0);
      if (variance > 0) {
        return 'text-success fw-bold';
      } else if (variance < 0) {
        return 'text-danger fw-bold';
      }
      return 'text-muted';
    }
  }
};
</script>

<style scoped>
.modal {
  z-index: 1055;
}

.modal-backdrop {
  z-index: 1054;
}

.table th,
.table td {
  padding: 0.75rem 0.5rem;
  font-size: 0.875rem;
  vertical-align: middle;
}

.table th {
  font-weight: 600;
  white-space: nowrap;
}

.badge {
  font-size: 0.7rem;
  padding: 0.35em 0.65em;
}

.avatar-sm {
  width: 2rem;
  height: 2rem;
}

.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 0.7rem;
  font-weight: 600;
}

.card {
  border: none;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.text-success {
  font-weight: 600;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.small {
  font-size: 0.8rem;
}

@media (max-width: 768px) {
  .table-responsive {
    font-size: 0.75rem;
  }

  .table th,
  .table td {
    padding: 0.5rem 0.25rem;
  }

  .avatar-sm {
    width: 1.5rem;
    height: 1.5rem;
  }
}
</style>